import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { IoIosMore } from "react-icons/io"
import { IoTrashOutline } from "react-icons/io5"
import { useNavigate } from "react-router-dom"

import {
  createOrUpdatePluginInstance,
  getPluginDetails,
} from "@fuse/api/pluginApi"
import { useEventPlugins } from "@fuse/hooks/useEventPlugins"
import PluginRenderer from "@fuse/sharedComponents/PluginRenderer"
import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import { get } from "lodash-es"
import { useRecoilState, useRecoilValue } from "recoil"
import { v4 as uuidv4 } from "uuid"

import { eventDetailsState } from "@fuse/recoil/events"
import { EnabledPlugin, Plugin } from "@fuse/recoil/plugins"
import { UserRole } from "@fuse/recoil/pluginStudio/types"
import { userState } from "@fuse/recoil/signup"

import { renderIconByName } from "@fuse/utils/pluginConnectionHelpers"
import { getTranspiledContent } from "@fuse/utils/pluginHelpers"
import { isInstantPlugin } from "@fuse/utils/pluginHelpers"

// Legacy hardcoded plugins that require joining (kept for backward compatibility)
const LEGACY_PLUGINS_REQUIRING_JOIN = [
  "7d28bdb0-5680-11f0-ad9d-955a5409cb36",
  "a96f8be0-5669-11f0-ad9d-955a5409cb36",
]

// API response type
interface PluginDetailsResponse {
  plugin: Plugin
  configuration?: any
}

interface EventPluginsLoaderProps {
  eventId: string
  enabledPlugins: EnabledPlugin[]
  userRole: UserRole
  pluginData?: Record<string, Plugin>
  onPluginClick?: (pluginId: string) => void
  user?: {
    uid?: string
    isLoggedIn?: boolean
    displayName?: string
    email?: string
    [key: string]: any
  }
  eventMetadata?: {
    name?: string
    description?: string
    timing?: {
      eventStartTime?: string
      eventEndTime?: string
    }
    location?: {
      eventLocation?: string
    }
    flyerImage?: string
  }
  showUserViewConfig?: boolean
  isHost?: boolean
  isEventDetailsPage?: boolean
  hasJoined?: boolean
  onJoinStatusChange?: (hasJoined: boolean) => void
}

const EventPluginsLoader = ({
  eventId,
  enabledPlugins,
  userRole,
  pluginData: initialPluginData,
  onPluginClick,
  user: providedUser,
  eventMetadata,
  showUserViewConfig,
  isHost,
  isEventDetailsPage = false,
  hasJoined = false,
  onJoinStatusChange,
}: EventPluginsLoaderProps) => {
  const recoilUser = useRecoilValue(userState)
  const { eventPlugins, loadEventPlugins } = useEventPlugins()
  const navigate = useNavigate()
  const renderCount = useRef(0)
  const prevPropsRef = useRef<any>({})
  const loadingRef = useRef(false)
  const [eventDetails, setEventDetails] = useRecoilState(eventDetailsState)

  // Track join status locally to ensure immediate updates
  const [localHasJoined, setLocalHasJoined] = useState(hasJoined)

  // Update local join status when prop changes
  useEffect(() => {
    setLocalHasJoined(hasJoined)
  }, [hasJoined])

  // Expose a method to update join status externally
  useEffect(() => {
    if (onJoinStatusChange) {
      // Store the callback in a ref so it can be called from outside
      ;(window as any).updatePluginJoinStatus = (newHasJoined: boolean) => {
        setLocalHasJoined(newHasJoined)
        onJoinStatusChange(newHasJoined)
      }
    }

    return () => {
      if ((window as any).updatePluginJoinStatus) {
        delete (window as any).updatePluginJoinStatus
      }
    }
  }, [onJoinStatusChange])

  // Use provided user if available, otherwise fall back to Recoil state
  const user = providedUser || recoilUser

  // Track errors at the component level
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showPluginMenu, setShowPluginMenu] = useState<string | null>(null)
  const [disableLoading, setDisableLoading] = useState<string | null>(null)

  // Memoize the plugin IDs to prevent unnecessary recalculations
  const pluginIds = useMemo(
    () => enabledPlugins?.map((plugin) => plugin.id) || [],
    [enabledPlugins],
  )

  // Increment render count and log detailed info
  renderCount.current++

  console.log(`[PLUGIN] EventPluginsLoader render #${renderCount.current}`, {
    eventId,
    enabledPluginsCount: enabledPlugins?.length || 0,
    pluginIds: enabledPlugins?.map((p) => p.id) || [],
    userRole,
    hasInitialPluginData: !!initialPluginData,
    initialPluginDataKeys: initialPluginData
      ? Object.keys(initialPluginData)
      : [],
    userId: user?.uid || "none",
    isLoggedIn: !!user?.isLoggedIn,
    hasJoined, // Original prop
    localHasJoined, // Local state
    isHost,
    timestamp: new Date().toISOString(),
  })

  // Track prop changes
  const currentProps = {
    eventId,
    enabledPluginsCount: enabledPlugins?.length || 0,
    pluginIds: JSON.stringify(enabledPlugins?.map((p) => p.id)?.sort() || []),
    userRole,
    hasInitialPluginData: !!initialPluginData,
    initialPluginDataCount: initialPluginData
      ? Object.keys(initialPluginData).length
      : 0,
    userId: user?.uid || "none",
    isLoggedIn: !!user?.isLoggedIn,
  }

  const prevProps = prevPropsRef.current
  const changedProps = Object.keys(currentProps).filter(
    (key) => prevProps[key] !== currentProps[key],
  )

  if (changedProps.length > 0 || renderCount.current === 1) {
    console.log(`[PLUGIN] EventPluginsLoader prop changes:`, {
      renderCount: renderCount.current,
      changedProps: changedProps.length > 0 ? changedProps : "First render",
      prevProps: renderCount.current > 1 ? prevProps : "N/A",
      currentProps,
      timestamp: new Date().toISOString(),
    })
  }

  prevPropsRef.current = currentProps

  // Handle login button click
  const handleLogin = useCallback(() => {
    console.log(`[PLUGIN] Login button clicked`, {
      eventId,
      timestamp: new Date().toISOString(),
    })
    // Redirect to login page
    window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`
  }, [eventId])

  // Handle plugin menu toggle
  const handlePluginMenuToggle = useCallback((pluginId: string) => {
    setShowPluginMenu((prev) => (prev === pluginId ? null : pluginId))
  }, [])

  // Handle configure plugin navigation
  const handleConfigurePlugin = useCallback(
    (pluginId: string) => {
      setShowPluginMenu(null)
      navigate(`/plugins/${pluginId}?event_id=${eventId}`)
    },
    [navigate, eventId],
  )

  // Handle plugin disable
  const handleDisablePlugin = useCallback(
    async (pluginId: string, pluginName: string) => {
      setDisableLoading(pluginId)
      setShowPluginMenu(null)

      // 1. START TRANSITION IMMEDIATELY - Remove plugin from UI
      const originalPlugins =
        (eventDetails.eventDetails?.eventDocData as any)?.enabled_plugins || []
      setEventDetails((prev) => ({
        ...prev,
        eventDetails: {
          ...prev.eventDetails,
          eventDocData: {
            ...prev.eventDetails.eventDocData,
            enabled_plugins:
              (prev.eventDetails.eventDocData as any)?.enabled_plugins?.filter(
                (p: any) => p.id !== pluginId,
              ) || [],
          },
        },
      }))

      try {
        // 2. Get current plugin configuration to preserve it
        const eventState = eventPlugins.events[eventId]
        const currentConfig = eventState?.configurations[pluginId] || {}

        // 3. Call backend API to disable the plugin (set enabled: false)
        const result = await createOrUpdatePluginInstance({
          eventId,
          pluginId,
          configuration: currentConfig,
          enabled: false,
        })

        if (!result.success) {
          throw new Error(result.error || "Failed to disable plugin")
        }

        // 4. SUCCESS TOAST ONLY ON TRUE SUCCESS
        showSuccessToast(`${pluginName} disabled from your event!`)
      } catch (error) {
        // 5. ROLLBACK on error - restore original plugins
        setEventDetails((prev) => ({
          ...prev,
          eventDetails: {
            ...prev.eventDetails,
            eventDocData: {
              ...prev.eventDetails.eventDocData,
              enabled_plugins: originalPlugins,
            },
          },
        }))

        console.error("Error disabling plugin:", error)
        showErrorToast("Failed to disable plugin")
      } finally {
        setDisableLoading(null)
      }
    },
    [eventId, eventDetails, setEventDetails, eventPlugins],
  )

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowPluginMenu(null)
    }

    if (showPluginMenu) {
      document.addEventListener("click", handleClickOutside)
      return () => document.removeEventListener("click", handleClickOutside)
    }
  }, [showPluginMenu])

  useEffect(() => {
    console.log(`[PLUGIN] EventPluginsLoader useEffect triggered`, {
      eventId,
      enabledPluginsCount: enabledPlugins?.length || 0,
      pluginIds: enabledPlugins?.map((p) => p.id) || [],
      hasInitialPluginData: !!initialPluginData,
      isCurrentlyLoading: loadingRef.current,
      renderCount: renderCount.current,
      timestamp: new Date().toISOString(),
    })

    // Skip if no event or plugins
    if (!eventId || !enabledPlugins || enabledPlugins.length === 0) {
      console.log(
        `[PLUGIN] EventPluginsLoader skipping - no plugins to load for event ${eventId}`,
        { enabledPlugins: enabledPlugins?.length || 0 },
      )
      return
    }

    // Skip if already loading
    if (loadingRef.current) {
      console.log(
        `[PLUGIN] EventPluginsLoader skipping - already loading plugins for event ${eventId}`,
        { renderCount: renderCount.current },
      )
      return
    }

    // Skip loading if plugins are pre-loaded via props
    if (
      initialPluginData &&
      Object.keys(initialPluginData).length >= enabledPlugins.length
    ) {
      console.log("[PLUGIN] EventPluginsLoader using pre-loaded plugin data:", {
        pluginsCount: Object.keys(initialPluginData).length,
        isLoggedIn: !!user?.isLoggedIn,
        pluginIds: Object.keys(initialPluginData),
        renderCount: renderCount.current,
      })
      return
    }

    // Set loading flag
    loadingRef.current = true
    console.log("[PLUGIN] EventPluginsLoader setting loading flag to true", {
      eventId,
      renderCount: renderCount.current,
    })

    // Load all plugins
    console.log("[PLUGIN] EventPluginsLoader starting plugin load:", {
      pluginCount: enabledPlugins.length,
      userRole,
      isLoggedIn: !!user?.isLoggedIn,
      pluginIds: enabledPlugins.map((p) => p.id),
      renderCount: renderCount.current,
      timestamp: new Date().toISOString(),
    })

    const pluginIds = enabledPlugins.map((plugin) => plugin.id)
    loadEventPlugins(eventId, pluginIds)
      .then(() => {
        console.log(
          "[PLUGIN] EventPluginsLoader plugin load completed successfully",
          {
            eventId,
            pluginIds,
            renderCount: renderCount.current,
          },
        )
      })
      .catch((error) => {
        console.error("[PLUGIN] EventPluginsLoader error loading plugins:", {
          error,
          eventId,
          pluginIds,
          renderCount: renderCount.current,
          stack: error instanceof Error ? error.stack : undefined,
        })
      })
      .finally(() => {
        console.log(
          "[PLUGIN] EventPluginsLoader setting loading flag to false",
          {
            eventId,
            renderCount: renderCount.current,
          },
        )
        loadingRef.current = false
      })

    return () => {
      loadingRef.current = false
      console.log("[PLUGIN] EventPluginsLoader effect cleanup for event:", {
        eventId,
        renderCount: renderCount.current,
        timestamp: new Date().toISOString(),
      })
    }
  }, [
    eventId,
    pluginIds,
    initialPluginData,
    loadEventPlugins,
    user?.isLoggedIn,
  ])

  // Helper function to detect if a plugin requires joining based on plugin characteristics
  const isPluginRequiringJoin = (
    pluginId: string,
    plugin?: Plugin,
  ): boolean => {
    const pluginName = plugin?.manifest?.name?.toLowerCase() || ""

    // Plugin ID-based detection for known zero-config plugins
    if (
      pluginId === "zero-config-announcements" ||
      pluginId === "announcements"
    ) {
      return true
    }
    if (pluginId === "photo-gallery" || pluginId === "alpha-photo-gallery") {
      return true
    }
    if (pluginId === "links" || pluginId === "zero-config-links") {
      return false // Links should be accessible to all
    }

    // Name-based detection as fallback
    if (pluginName.includes("announcement")) {
      return true
    }
    if (pluginName.includes("photo") || pluginName.includes("gallery")) {
      return true
    }
    if (pluginName.includes("link")) {
      return false
    }

    // Default: for zero-config plugins, require joining unless explicitly accessible
    if (plugin?.manifest?.requiresZeroConfiguration === true) {
      return true // Most zero-config plugins should require joining
    }

    return false
  }

  // Helper function to get contextual join required message based on plugin type
  const getJoinRequiredMessage = (pluginId: string, plugin?: Plugin) => {
    const pluginName = plugin?.manifest?.name?.toLowerCase() || "this plugin"

    // Special cases for known plugin types
    if (pluginName.includes("announcement")) {
      return "Join to see announcements from the host"
    } else if (pluginName.includes("photo") || pluginName.includes("gallery")) {
      return "Join to see event photos"
    } else {
      return `Join to see ${pluginName}`
    }
  }

  // Helper function to render a placeholder while plugin is loading or if there's an error
  const renderPluginPlaceholder = (
    pluginId: string,
    isLoading: boolean,
    error?: string,
    plugin?: Plugin,
    requiresJoin?: boolean,
  ) => {
    // Check if error is an authentication error or join requirement
    const isAuthError =
      error &&
      (error.includes("Authentication required") ||
        error.includes("unauthenticated") ||
        error.includes("log in"))

    const isJoinRequired = requiresJoin && !localHasJoined

    return (
      <div
        key={pluginId}
        className={`relative p-4 bg-white/10 border border-white/20 rounded-2xl ${isAuthError || isJoinRequired ? "" : "h-48 items-center justify-center backdrop-blur-sm"} flex overflow-hidden`}
      >
        {isLoading ? (
          <div className="animate-pulse flex space-x-4 w-full">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-white/35 rounded-md w-3/4 animate-pulse"></div>
              <div className="space-y-3">
                <div className="h-3 bg-white/8 rounded-md animate-pulse"></div>
                <div className="h-3 bg-white/8 rounded-md w-5/6 animate-pulse"></div>
                <div className="h-3 bg-white/6 rounded-md w-4/6 animate-pulse"></div>
              </div>
              <div className="flex space-x-2 pt-2">
                <div className="h-8 w-16 bg-white/8 rounded-md animate-pulse"></div>
                <div className="h-8 w-20 bg-white/6 rounded-md animate-pulse"></div>
              </div>
            </div>
          </div>
        ) : error || isJoinRequired ? (
          <div
            className={
              isAuthError || isJoinRequired
                ? "flex w-full text-center"
                : "text-red-400/80"
            }
          >
            {isAuthError || isJoinRequired ? (
              <div className="flex flex-col w-full">
                <div className="flex flex-row gap-3 items-center mb-3">
                  {renderIconByName(plugin?.manifest?.iconName)}
                  <p className="text-white35 flex flex-1 text-start">
                    {plugin?.manifest?.label || plugin?.manifest?.name}
                  </p>
                </div>

                <div className="text-white/40 text-sm flex w-full items-center justify-center text-center">
                  {isJoinRequired
                    ? getJoinRequiredMessage(pluginId, plugin)
                    : `Join to see the ${(plugin?.manifest?.label || plugin?.manifest?.name)?.toLowerCase()}`}
                </div>
              </div>
            ) : (
              <>
                <div className="text-red-400/80 text-sm">
                  Plugin temporarily unavailable
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="text-white/40 text-sm">Plugin not available</div>
        )}
      </div>
    )
  }

  // Helper function to determine which view to show for a plugin
  const getPluginUserRole = (pluginId: string, plugin?: Plugin): UserRole => {
    if (!isHost) return UserRole.GUEST

    // Check if plugin explicitly defines whether to show host view in event details
    // BUT only apply this setting when we're on the event details page
    if (
      isEventDetailsPage &&
      plugin?.manifest?.showHostViewInDetails !== undefined
    ) {
      const role = plugin.manifest.showHostViewInDetails
        ? UserRole.HOST_EVENT_VIEW
        : UserRole.GUEST
      console.log(
        `[PLUGIN_VIEW] Plugin ${pluginId} using ${role} (explicit showHostViewInDetails: ${plugin.manifest.showHostViewInDetails}, eventDetailsPage: ${isEventDetailsPage})`,
        {
          pluginName: plugin?.manifest?.name,
          showHostViewInDetails: plugin.manifest.showHostViewInDetails,
          isEventDetailsPage,
        },
      )
      return role
    }

    // For non-event-details pages, hosts should see host view by default
    if (!isEventDetailsPage) {
      console.log(
        `[PLUGIN_VIEW] Plugin ${pluginId} using HOST_EVENT_VIEW (host on non-event-details page)`,
        {
          pluginName: plugin?.manifest?.name,
          isEventDetailsPage,
        },
      )
      return UserRole.HOST_EVENT_VIEW
    }

    // For zero-config plugins without explicit setting, show host view by default
    if (plugin?.manifest?.requiresZeroConfiguration === true) {
      console.log(
        `[PLUGIN_VIEW] Plugin ${pluginId} using HOST_EVENT_VIEW (zero-config default)`,
        {
          pluginName: plugin?.manifest?.name,
          requiresZeroConfig: plugin?.manifest?.requiresZeroConfiguration,
        },
      )
      return UserRole.HOST_EVENT_VIEW
    }

    // Otherwise use the default setting
    const defaultRole = showUserViewConfig
      ? UserRole.GUEST
      : UserRole.HOST_EVENT_VIEW
    console.log(
      `[PLUGIN_VIEW] Plugin ${pluginId} using ${defaultRole} (fallback setting)`,
      {
        pluginName: plugin?.manifest?.name,
        defaultSetting: showUserViewConfig,
      },
    )
    return defaultRole
  }

  // Memoize plugin/config objects for each enabled plugin
  const memoizedPlugins = useMemo(() => {
    const map: Record<string, Plugin> = {}
    enabledPlugins.forEach((enabledPlugin) => {
      const pluginId = enabledPlugin.id
      const eventState = eventPlugins.events[eventId]
      const plugin = eventState?.plugins[pluginId]
      if (plugin) {
        map[pluginId] = plugin
      }
    })
    return map
  }, [enabledPlugins, eventPlugins.events, eventId])

  const memoizedConfigs = useMemo(() => {
    const map: Record<string, any> = {}
    enabledPlugins.forEach((enabledPlugin) => {
      const pluginId = enabledPlugin.id
      const eventState = eventPlugins.events[eventId]
      const config = eventState?.configurations[pluginId]
      if (config) {
        map[pluginId] = config
      }
    })
    return map
  }, [enabledPlugins, eventPlugins.events, eventId])

  // Modify the renderPlugin function to use the new role determination
  const renderPlugin = (pluginId: string, plugin: Plugin, config: any) => {
    const effectiveUserRole = getPluginUserRole(pluginId, plugin)
    const isInstant = isInstantPlugin(plugin)

    // Check if plugin requires authentication and user is not logged in
    const requiresAuth = plugin?.manifest?.requiresAuthentication === true
    const isLoggedIn = !!user?.isLoggedIn

    // Check if this is a plugin that requires joining
    // Use multiple detection methods since database plugins may not have requiresJoinToView property
    const requiresJoin =
      plugin?.manifest?.requiresJoinToView === true || // Explicit property (preferred)
      LEGACY_PLUGINS_REQUIRING_JOIN.includes(pluginId) || // Legacy hardcoded IDs
      isPluginRequiringJoin(pluginId, plugin) // Fallback detection based on plugin characteristics

    // Debug: Log the full plugin manifest to see what properties are available
    console.log(`[PLUGIN_DEBUG] Plugin ${pluginId} manifest:`, {
      pluginName: plugin?.manifest?.name,
      manifest: plugin?.manifest,
      requiresJoinToViewValue: plugin?.manifest?.requiresJoinToView,
      requiresJoinToViewType: typeof plugin?.manifest?.requiresJoinToView,
      requiresJoin,
      pluginId,
      detectionMethods: {
        explicitProperty: plugin?.manifest?.requiresJoinToView === true,
        legacyList: LEGACY_PLUGINS_REQUIRING_JOIN.includes(pluginId),
        fallbackDetection: isPluginRequiringJoin(pluginId, plugin),
      },
    })

    if (requiresAuth && !isLoggedIn) {
      return renderPluginPlaceholder(
        pluginId,
        false,
        "Authentication required to view this plugin",
        plugin,
      )
    }

    // If plugin requires joining and user hasn't joined, show placeholder
    // But don't show this for hosts - they should always see their plugins
    if (requiresJoin && !localHasJoined && !isHost) {
      console.log(
        `[PLUGIN_JOIN] Plugin ${pluginId} requires join - showing placeholder`,
        {
          pluginName: plugin?.manifest?.name,
          requiresJoin,
          localHasJoined,
          isHost,
          requiresJoinToView: plugin?.manifest?.requiresJoinToView,
          isInLegacyList: LEGACY_PLUGINS_REQUIRING_JOIN.includes(pluginId),
        },
      )
      return renderPluginPlaceholder(pluginId, false, undefined, plugin, true)
    }

    console.log(`[PLUGIN_JOIN] Plugin ${pluginId} passed join check`, {
      pluginName: plugin?.manifest?.name,
      requiresJoin,
      localHasJoined,
      isHost,
      requiresJoinToView: plugin?.manifest?.requiresJoinToView,
      isInLegacyList: LEGACY_PLUGINS_REQUIRING_JOIN.includes(pluginId),
    })

    const renderPluginHeader = () => {
      return (
        <div className="flex flex-row gap-3 items-center mb-3 justify-center">
          {renderIconByName(plugin?.manifest?.iconName)}
          <p className="text-white35 flex flex-1 text-start">
            {plugin?.manifest?.label || plugin?.manifest?.name}
          </p>

          {/* Only show the triple dot menu if NOT on EventDetails page */}
          {isHost && !isEventDetailsPage && (
            <div className="z-10">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handlePluginMenuToggle(pluginId)
                }}
                type="button"
                className="p-1.5 rounded bg-transparent transition-all duration-200 group"
                title="Plugin options"
              >
                <IoIosMore className="text-white35" />
              </button>

              {showPluginMenu === pluginId && (
                <div
                  className="absolute top-10 right-4 z-20 animate-in fade-in-0 zoom-in-95 duration-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  {!isInstant && (
                    <button
                      type="button"
                      onClick={() => handleConfigurePlugin(pluginId)}
                      className="w-full text-left px-3 py-2 text-sm text-white bg-black/80 backdrop-blur-sm border border-gray-600/50 rounded-lg mb-1 hover:bg-black/90 transition-colors flex items-center space-x-2 shadow-lg min-w-32"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span className="font-medium">Configure</span>
                    </button>
                  )}

                  <button
                    onClick={() =>
                      handleDisablePlugin(
                        pluginId,
                        plugin.manifest?.name || "Plugin",
                      )
                    }
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm text-red-300 bg-red-900/80 backdrop-blur-sm border border-red-600/50 rounded-lg hover:bg-red-900/90 hover:text-red-200 transition-colors flex items-center space-x-2 shadow-lg"
                    disabled={disableLoading === pluginId}
                  >
                    {disableLoading === pluginId ? (
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-red-300/30 border-t-red-300"></div>
                    ) : (
                      <IoTrashOutline />
                    )}
                    <span className="font-medium">
                      {disableLoading === pluginId ? "Removing..." : "Remove"}
                    </span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )
    }

    return (
      <div
        key={pluginId}
        onClick={() => onPluginClick && onPluginClick(pluginId)}
        className={`relative ${onPluginClick ? "cursor-pointer" : ""}`}
      >
        {disableLoading === pluginId && (
          <div className="absolute inset-0 backdrop-blur-sm bg-black/40 flex items-center justify-center z-10 rounded-lg">
            <div className="animate-spin rounded-full h-10 w-10 border-3 border-white/30 border-t-white shadow-lg"></div>
          </div>
        )}

        <PluginRenderer
          plugin={memoizedPlugins[pluginId] || plugin}
          config={memoizedConfigs[pluginId] || config}
          eventId={eventId}
          userId={user?.uid || ""}
          userRole={effectiveUserRole}
          eventMetadata={eventMetadata}
          isLoggedIn={!!user?.isLoggedIn}
          renderPluginHeader={renderPluginHeader}
        />
      </div>
    )
  }

  // Render all plugins
  return (
    <div className="space-y-4">
      {enabledPlugins.map((enabledPlugin) => {
        const pluginId = enabledPlugin.id

        // Check if we have pre-loaded plugin data
        if (initialPluginData && initialPluginData[pluginId]) {
          return renderPlugin(
            pluginId,
            initialPluginData[pluginId],
            {}, // No config for pre-loaded plugins
          )
        }

        // Get plugin from event plugins state
        const eventState = eventPlugins.events[eventId]
        const plugin = eventState?.plugins[pluginId]
        const config = eventState?.configurations[pluginId]
        const isLoading = eventState?.isLoading || false
        const hasError = eventState?.hasError || false
        const errorMessage = eventState?.errorMessage || errors[pluginId]
        const pluginError = eventState?.pluginErrors?.[pluginId]

        // If plugin is loaded, render it
        if (plugin && !pluginError) {
          return renderPlugin(pluginId, plugin, config)
        }

        // Don't render plugin while loading, even if plugin data exists (prevents race condition)
        if (isLoading && !plugin) {
          return renderPluginPlaceholder(pluginId, true, undefined, plugin)
        }

        // Otherwise render placeholder
        return renderPluginPlaceholder(
          pluginId,
          false,
          pluginError || (hasError ? errorMessage : undefined),
          plugin,
        )
      })}
    </div>
  )
}

export default EventPluginsLoader

/**
 * Component that handles event plugin rendering logic
 * Extracted from EventDetails to prevent function recreation issues
 */
interface EventPluginsRendererProps {
  eventDocData: any
  eventId: string
  user: any
  isAdmin?: boolean
  unCachedLocation?: any
  showUserViewConfig?: boolean
  isEventDetailsPage?: boolean
  hasJoined?: boolean
  onJoinStatusChange?: (hasJoined: boolean) => void
}

export const EventPluginsRenderer = memo<EventPluginsRendererProps>(
  ({
    eventDocData,
    eventId,
    user,
    isAdmin = false,
    unCachedLocation,
    showUserViewConfig,
    isEventDetailsPage = false,
    hasJoined = false,
    onJoinStatusChange,
  }) => {
    const renderCount = useRef(0)
    const prevPropsRef = useRef<any>({})

    renderCount.current++

    // console.log(
    //   `[PLUGIN] EventPluginsRenderer render #${renderCount.current}`,
    //   {
    //     eventId,
    //     userId: user?.uid || "none",
    //     isAdmin,
    //     hasEventDocData: !!eventDocData,
    //     hasUnCachedLocation: !!unCachedLocation,
    //     timestamp: new Date().toISOString(),
    //   },
    // )

    // Track prop changes for EventPluginsRenderer
    const currentProps = {
      eventId,
      userId: user?.uid || "none",
      isAdmin,
      hasEventDocData: !!eventDocData,
      eventDocDataKeys: eventDocData ? Object.keys(eventDocData).length : 0,
      enabledPluginsCount: eventDocData
        ? get(eventDocData, "enabled_plugins", [])?.length
        : 0,
      hasUnCachedLocation: !!unCachedLocation,
    }

    const prevProps = prevPropsRef.current
    const changedProps = Object.keys(currentProps).filter(
      (key) => prevProps[key] !== currentProps[key],
    )

    if (changedProps.length > 0 || renderCount.current === 1) {
      console.log(`[PLUGIN] EventPluginsRenderer prop changes:`, {
        renderCount: renderCount.current,
        changedProps: changedProps.length > 0 ? changedProps : "First render",
        prevProps: renderCount.current > 1 ? prevProps : "N/A",
        currentProps,
        timestamp: new Date().toISOString(),
      })
    }

    prevPropsRef.current = currentProps

    if (!eventDocData || !eventId) {
      console.log(`[PLUGIN] EventPluginsRenderer early return - missing data`, {
        hasEventDocData: !!eventDocData,
        eventId,
        renderCount: renderCount.current,
      })
      return null
    }

    const initiatorUid = get(eventDocData, "initiatorUid", "")
    const coHosts = get(eventDocData, "coHosts", {}) as Record<string, boolean>
    const enabledPlugins = get(
      eventDocData,
      "enabled_plugins",
      [],
    ) as EnabledPlugin[]

    console.log(`[PLUGIN] EventPluginsRenderer plugin data analysis:`, {
      initiatorUid,
      coHostsCount: Object.keys(coHosts)?.length,
      enabledPluginsCount: enabledPlugins?.length,
      enabledPluginIds: enabledPlugins?.map((p) => p.id),
      renderCount: renderCount.current,
    })

    // Check if user is a host or creator
    const isCreator = initiatorUid === user?.uid
    const isCoHost = user?.uid ? Object.keys(coHosts).includes(user.uid) : false
    const isHost = isCreator || isCoHost || !!isAdmin

    console.log(`[PLUGIN] EventPluginsRenderer user role analysis:`, {
      userId: user?.uid || "none",
      initiatorUid,
      isCreator,
      isCoHost,
      isHost,
      isAdmin,
      renderCount: renderCount.current,
    })

    console.log("eventDocData", eventDocData)

    // Only render if there are plugins to display
    if (!enabledPlugins || enabledPlugins.length === 0) {
      console.log(
        `[PLUGIN] EventPluginsRenderer early return - no plugins to display`,
        {
          enabledPluginsLength: enabledPlugins?.length || 0,
          renderCount: renderCount.current,
        },
      )
      return null
    }

    // Construct user data with event-specific context
    const contextualUserData = user
      ? {
          ...user,
          isEventCreator: isCreator,
          isEventCoHost: isCoHost,
          eventPermissions: {
            canEdit: isAdmin || isCreator,
            canInvite: isAdmin || isCreator || isCoHost,
            canViewGuests: true,
          },
        }
      : undefined

    // Create event metadata from eventDocData
    const eventMetadata = {
      name: eventDocData.eventName,
      description: eventDocData.eventDescription,
      timing: eventDocData.timing,
      location: {
        eventLocation:
          unCachedLocation?.eventLocation ||
          eventDocData.location?.eventLocation,
      },
      flyerImage: eventDocData.flyerImage || "",
    }

    console.log(
      `[PLUGIN] EventPluginsRenderer passing props to EventPluginsLoader:`,
      {
        eventId,
        enabledPluginsCount: enabledPlugins.length,
        userRole: isHost ? "HOST_EVENT_VIEW" : "GUEST",
        hasContextualUserData: !!contextualUserData,
        eventMetadataName: eventMetadata.name,
        renderCount: renderCount.current,
      },
    )

    const editablePluginsIds = [
      "7d28bdb0-5680-11f0-ad9d-955a5409cb36",
      "a96f8be0-5669-11f0-ad9d-955a5409cb36",
    ]

    console.log("enabledPlugins", enabledPlugins)

    return (
      <EventPluginsLoader
        eventId={eventId}
        enabledPlugins={enabledPlugins}
        userRole={isHost ? UserRole.HOST_EVENT_VIEW : UserRole.GUEST}
        user={contextualUserData}
        eventMetadata={eventMetadata}
        showUserViewConfig={showUserViewConfig}
        isHost={isHost}
        isEventDetailsPage={isEventDetailsPage}
        hasJoined={hasJoined}
        onJoinStatusChange={onJoinStatusChange}
      />
    )
  },
  // Custom comparison function to prevent re-renders when object references change but content is the same
  (prevProps, nextProps) => {
    // Compare primitive props
    if (prevProps.eventId !== nextProps.eventId) return false
    if (prevProps.isAdmin !== nextProps.isAdmin) return false

    // Compare user by uid (most important identifier)
    if (prevProps.user?.uid !== nextProps.user?.uid) return false

    // Compare eventDocData by key properties that matter for plugins
    if (!prevProps.eventDocData && !nextProps.eventDocData) return true
    if (!prevProps.eventDocData || !nextProps.eventDocData) return false

    const prevPlugins = get(prevProps.eventDocData, "enabled_plugins", [])
    const nextPlugins = get(nextProps.eventDocData, "enabled_plugins", [])
    if (prevPlugins?.length !== nextPlugins?.length) return false

    // Compare plugin IDs (shallow comparison of the array contents)
    for (let i = 0; i < prevPlugins?.length; i++) {
      if (prevPlugins[i]?.id !== nextPlugins[i]?.id) return false
    }

    // Compare unCachedLocation by eventLocation property
    const prevLocation = prevProps.unCachedLocation?.eventLocation
    const nextLocation = nextProps.unCachedLocation?.eventLocation
    if (prevLocation !== nextLocation) return false

    // If we get here, props are effectively the same
    return true
  },
)
